import React, { useState } from 'react';
import { Button, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { BellOutlined, CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import StatusSelect from '../components/StatusSelect';
import SoftTag, { OfferTypeTag } from '../components/SoftTag';
import {
  INQUIRY_STATUSES,
  OFFER_STATUSES,
  ORDER_STATUSES,
  QUOTE_STATUSES,
  PRIORITY_TONES,
  getInquiryStatusTone,
  getQuoteStatusTone,
  getOfferStatusTone,
  getOrderStatusTone,
} from '../constants/statuses';
import {
  buildOrdersPageRecords,
  getQuotedAmount,
  getQuoteSupplierNames,
  getOrdersPagePriorityValue,
} from '../utils/ordersPageHelpers';
import { useI18n } from '../i18n';
import { brandSemantic } from '../config/brand';

const { Option } = Select;
const { Title } = Typography;
const { confirm } = Modal;

const STATUS_OPTIONS_BY_TYPE = {
  Inquiry: INQUIRY_STATUSES,
  Quote: QUOTE_STATUSES,
  Offer: OFFER_STATUSES,
  Order: ORDER_STATUSES,
};

export default function OrdersPage({
  offers,
  inquiries = [],
  quotes = [],
  suppliers = [],
  leads,
  offerStatusFilter,
  setOfferStatusFilter,
  getOfferStatus,
  updateOfferStatus,
  getFollowUpsForOffer,
  getOverdueCount,
  getOfferSupplierSections,
  showModal,
  openLeadProfile,
  setCurrentView,
  openOrderDetails,
  openInquiryDetail,
  openQuoteDetail,
  isSalesUser = false,
  onCreateInquiry,
  canDelete = true,
  typeFilter = null,
  title = 'Sales Quotes / Orders',
  returnView = 'offers',
  showCreateInquiry = false,
  showCreateOfferOrder = true,
}) {
  const { t } = useI18n();
  const [orderSearch, setOrderSearch] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [orderPriorityFilter, setOrderPriorityFilter] = useState('all');
  const [offerSearch, setOfferSearch] = useState('');
  const allRecords = buildOrdersPageRecords(offers, inquiries, quotes)
    .filter((record) => !typeFilter || record.type === typeFilter)
    .filter((record) => !record.archived);
  const statusOptions = STATUS_OPTIONS_BY_TYPE[typeFilter] || OFFER_STATUSES;
  const statusFilteredRecords = offerStatusFilter === 'all'
    ? allRecords
    : allRecords.filter((record) => (
      record.type === 'Inquiry' || record.type === 'Quote'
        ? record.status === offerStatusFilter
        : getOfferStatus(record.id, record.status) === offerStatusFilter
    ));
  const filteredRecords = typeFilter === 'Order'
    ? statusFilteredRecords.filter((record) => {
      const query = orderSearch.trim().toLowerCase();
      const matchesSearch = !query || [record.id, record.client, record.responsible].some((value) => String(value || '').toLowerCase().includes(query));
      const orderType = record.sourceOfferId ? 'Re-order' : 'New Order';
      const matchesType = orderTypeFilter === 'all' || orderType === orderTypeFilter;
      const matchesPriority = orderPriorityFilter === 'all' || (record.priority || 'Medium') === orderPriorityFilter;
      return matchesSearch && matchesType && matchesPriority;
    })
    : typeFilter === 'Offer'
      ? statusFilteredRecords.filter((record) => {
        const query = offerSearch.trim().toLowerCase();
        const matchesSearch = !query || [record.id, record.client, record.responsible].some((value) => String(value || '').toLowerCase().includes(query));
        return matchesSearch;
      })
      : statusFilteredRecords;

  const openRecordDetail = (record) => {
    if (record.type === 'Inquiry') return openInquiryDetail?.(record, returnView);
    if (record.type === 'Quote') return openQuoteDetail?.(record, returnView);
    return openOrderDetails(record, returnView);
  };

  const standardColumns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 135,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => openRecordDetail(record)}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 95,
      render: (type) => <OfferTypeTag type={type} />,
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority, record) => (
        getOrdersPagePriorityValue(record)
          ? <SoftTag tone={PRIORITY_TONES[priority] || PRIORITY_TONES.Medium}>{t(priority)}</SoftTag>
          : <span style={{ color: '#bfbfbf' }}>—</span>
      ),
    },
    {
      title: t('Client Name'),
      dataIndex: 'client',
      key: 'client',
      render: (text, record) => {
        const lead = leads.find(l => l.id === record.clientId);
        return (
          <Button
            type="link"
            onClick={() => { if (lead) { openLeadProfile(lead, returnView); } }}
            style={{ padding: 0 }}
          >
            {text}
          </Button>
        );
      },
    },
    {
      title: t('Suppliers'),
      key: 'suppliers',
      width: 220,
      render: (_, record) => {
        if (record.type === 'Inquiry') return <span style={{ color: '#bfbfbf' }}>—</span>;
        if (record.type === 'Quote') {
          const names = getQuoteSupplierNames(record);
          if (names.length === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
          return (
            <Space wrap size={4}>
              {names.map((name) => (
                <Tag key={name} style={{ fontSize: 11, margin: 0 }}>{name}</Tag>
              ))}
            </Space>
          );
        }
        const sups = getOfferSupplierSections(record.id);
        if (!sups || sups.length === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Space direction="vertical" size={2}>
            <Space wrap size={4}>
              {sups.map(s => (
                <Tag key={s.name} style={{ fontSize: 11, margin: 0 }}>{s.name}</Tag>
              ))}
            </Space>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
              onClick={() => showModal('viewSuppliers', record)}
            >
              {t('View details')}
            </Button>
          </Space>
        );
      },
    },
    {
      title: t('Amount'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (val, record) => {
        if (record.type !== 'Inquiry' && record.type !== 'Quote') {
          return <span style={{ fontWeight: 500 }}>{val}</span>;
        }
        const amount = getQuotedAmount(record, suppliers);
        return amount === null
          ? <span style={{ color: '#bfbfbf' }}>—</span>
          : <span style={{ fontWeight: 500 }}>{amount.toLocaleString('da-DK')} DKK</span>;
      },
    },
    {
      title: t('Follow-ups'),
      key: 'followUps',
      width: 110,
      align: 'center',
      render: (_, record) => {
        if (record.type === 'Inquiry' || record.type === 'Quote') return <span style={{ color: '#bfbfbf' }}>—</span>;
        const items = getFollowUpsForOffer(record.id);
        const overdue = getOverdueCount(record.id);
        if (items.length === 0) {
          return (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ padding: 0 }}
              onClick={() => showModal('viewFollowUps', record, { startAdding: true })}
            >
              {t('Add')}
            </Button>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => showModal('viewFollowUps', record)}
          >
            <Space size={4} align="center">
              <BellOutlined style={{ color: brandSemantic.primary, fontSize: 14, lineHeight: 1 }} />
              <span style={{ fontWeight: 600 }}>{items.length}</span>
              {overdue > 0 && (
                <SoftTag
                  tone={{ color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder }}
                  style={{
                    minHeight: 20,
                    lineHeight: '18px',
                    padding: '0 6px',
                    fontSize: 11,
                  }}
                >
                  !{overdue}
                </SoftTag>
              )}
            </Space>
          </Button>
        );
      },
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => {
        if (record.type === 'Inquiry') {
          return <SoftTag tone={getInquiryStatusTone(status)}>{t(status)}</SoftTag>;
        }
        if (record.type === 'Quote') {
          return <SoftTag tone={getQuoteStatusTone(status)}>{t(status)}</SoftTag>;
        }
        const current = getOfferStatus(record.id, status);
        const isOrder = record.type === 'Order';
        return (
          <StatusSelect
            value={current}
            onChange={(val) => updateOfferStatus(record.id, val)}
            statuses={isOrder ? ORDER_STATUSES : OFFER_STATUSES}
            getTone={isOrder ? getOrderStatusTone : getOfferStatusTone}
            width={Math.min(Math.max(current.length * 7 + 34, 120), 260)}
          />
        );
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        record.type === 'Inquiry' || record.type === 'Quote' ? (
          <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => openRecordDetail(record)}>
            {t('Open')}
          </Button>
        ) : (
          <Space direction="vertical" size={2}>
            <Button type="link" size="small" icon={<EditOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => showModal('editOffer', record)}>{t('Edit')}</Button>
            <Button type="link" size="small" icon={<CopyOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => showModal('repeatOffer', record)}>{t('Repeat')}</Button>
            {canDelete && (
              <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: 0, height: 'auto' }}
                onClick={() => confirm({ title: t('Delete'), content: t('Are you sure you want to delete this entry?'), onOk() { message.success(t('Deleted')); } })}>
                {t('Delete')}
              </Button>
            )}
          </Space>
        )
      ),
    },
  ];

  const offerColumns = [
    {
      title: t('Quote ID'),
      dataIndex: 'id',
      key: 'id',
      width: 160,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => openRecordDetail(record)}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('Client Name'),
      dataIndex: 'client',
      key: 'client',
      render: (name, record) => {
        const lead = leads.find((item) => item.id === record.clientId);
        return (
          <Button
            type="link"
            onClick={() => { if (lead) openLeadProfile(lead, returnView); }}
            style={{ padding: 0 }}
          >
            {name}
          </Button>
        );
      },
    },
    {
      title: t('Offer Date'),
      dataIndex: 'date',
      key: 'date',
      width: 135,
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => {
        const current = getOfferStatus(record.id, status);
        return <SoftTag tone={getOfferStatusTone(current)}>{t(current)}</SoftTag>;
      },
    },
    {
      title: t('Responsible'),
      dataIndex: 'responsible',
      key: 'responsible',
      width: 170,
      render: (responsible) => responsible || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button type="link" size="small" icon={<EditOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => showModal('editOffer', record)}>{t('Edit')}</Button>
          <Button type="link" size="small" icon={<CopyOutlined />} style={{ padding: 0, height: 'auto' }} onClick={() => showModal('repeatOffer', record)}>{t('Repeat')}</Button>
          {canDelete && (
            <Button type="link" size="small" danger icon={<DeleteOutlined />} style={{ padding: 0, height: 'auto' }}
              onClick={() => confirm({ title: t('Delete'), content: t('Are you sure you want to delete this entry?'), onOk() { message.success(t('Deleted')); } })}>
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];
  const orderColumns = [
    {
      title: t('Order ID'),
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (id, record) => (
        <Button type="link" onClick={() => openRecordDetail(record)} style={{ padding: 0, fontWeight: 600 }}>
          {id}
        </Button>
      ),
    },
    {
      title: t('Client Name'),
      dataIndex: 'client',
      key: 'client',
      render: (name, record) => {
        const lead = leads.find((item) => item.id === record.clientId);
        return (
          <Button type="link" onClick={() => { if (lead) openLeadProfile(lead, returnView); }} style={{ padding: 0 }}>
            {name}
          </Button>
        );
      },
    },
    { title: t('Order Date'), dataIndex: 'date', key: 'date', width: 125 },
    { title: t('Requested Delivery'), dataIndex: 'deliveryDate', key: 'deliveryDate', width: 155, render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span> },
    {
      title: t('Order Type'),
      key: 'orderType',
      width: 120,
      render: (_, record) => <SoftTag tone={record.sourceOfferId ? PRIORITY_TONES.Low : PRIORITY_TONES.Medium}>{record.sourceOfferId ? t('Re-order') : t('New Order')}</SoftTag>,
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 190,
      render: (status, record) => {
        const current = getOfferStatus(record.id, status);
        return <SoftTag tone={getOrderStatusTone(current)}>{t(current)}</SoftTag>;
      },
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 110,
      render: (priority) => <SoftTag tone={PRIORITY_TONES[priority || 'Medium']}>{t(priority || 'Medium')}</SoftTag>,
    },
    { title: t('Total (excl. VAT)'), dataIndex: 'total', key: 'total', width: 145, render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span> },
    {
      title: t('Responsible'),
      dataIndex: 'responsible',
      key: 'responsible',
      width: 170,
      render: (responsible) => responsible || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    standardColumns.find((column) => column.key === 'actions'),
  ];
  const columns = typeFilter === 'Offer'
    ? offerColumns
    : typeFilter === 'Order'
      ? orderColumns
      : standardColumns;

  return (
    <div>
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t(title)}</Title>
        <div className="page-toolbar-controls">
          {typeFilter === 'Order' ? (
            <Space wrap size={12}>
              <Input
                value={orderSearch}
                onChange={(event) => setOrderSearch(event.target.value)}
                placeholder={t('Search by Order ID, Customer, PO No...')}
                suffix={<SearchOutlined />}
                style={{ width: 250, height: 32, display: 'flex', alignItems: 'center' }}
              />
              <Select value={offerStatusFilter} onChange={setOfferStatusFilter} style={{ width: 135 }} options={[{ value: 'all', label: t('All Statuses') }, ...statusOptions.map((status) => ({ value: status, label: t(status) }))]} />
              <Select value={orderTypeFilter} onChange={setOrderTypeFilter} style={{ width: 135 }} options={[{ value: 'all', label: t('All Types') }, { value: 'New Order', label: t('New Order') }, { value: 'Re-order', label: t('Re-order') }]} />
              <Select value={orderPriorityFilter} onChange={setOrderPriorityFilter} style={{ width: 135 }} options={[{ value: 'all', label: t('All Priorities') }, ...['Low', 'Medium', 'High'].map((priority) => ({ value: priority, label: t(priority) }))]} />
            </Space>
          ) : typeFilter === 'Offer' ? (
            <Space wrap size={12}>
              <Input
                value={offerSearch}
                onChange={(event) => setOfferSearch(event.target.value)}
                placeholder={t('Search by Quote ID, Client Name...')}
                suffix={<SearchOutlined />}
                style={{ width: 250, height: 32, display: 'flex', alignItems: 'center' }}
              />
              <Select value={offerStatusFilter} onChange={setOfferStatusFilter} style={{ width: 180 }} options={[{ value: 'all', label: t('All Statuses') }, ...statusOptions.map((status) => ({ value: status, label: t(status) }))]} />
            </Space>
          ) : (
            <>
              <Select
                value={offerStatusFilter}
                onChange={setOfferStatusFilter}
                style={{ width: 260 }}
                size="large"
                placeholder={t('Filter by status')}
              >
                <Option value="all">{t('All statuses')}</Option>
                {statusOptions.map((status) => <Option key={status} value={status}>{t(status)}</Option>)}
              </Select>
              <Button size="large" onClick={() => setOfferStatusFilter('all')}>{t('Clear')}</Button>
            </>
          )}
          {isSalesUser && showCreateInquiry && (
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={onCreateInquiry}>
              {t('Create Inquiry')}
            </Button>
          )}
          {showCreateOfferOrder && (
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => showModal('createOfferOrder')}>
              {t('Create Offer/Order')}
            </Button>
          )}
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRecords}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10, position: ['bottomCenter'] }}
        bordered
        size="middle"
        locale={{ emptyText: t('No records match') }}
      />
    </div>
  );
}
